import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { getAuthToken } from '@/utils/auth';
import { useAuth } from '@/hooks/useAuth';
import {
  normalizeAuthRole,
  readRoleFromStorage,
  resolveProtectedRouteDecision,
} from '@/lib/roleRouting';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRoles?: string[];
}

function buildLoginRedirect(pathname: string, search: string): string {
  const target = `${pathname}${search}`;
  if (!target || target === '/login') return '/login';
  return `/login?redirect=${encodeURIComponent(target)}`;
}

export default function ProtectedRoute({ children, requiredRoles }: ProtectedRouteProps) {
  const location = useLocation();
  const { user, isLoading } = useAuth();
  const token = getAuthToken();

  // Preferir role do Context (já normalizada no login); fallback no storage
  // para cobrir o instante em que o token existe mas o Context ainda hidrata.
  const contextRole = normalizeAuthRole(user?.role);
  const storageRole =
    typeof window !== 'undefined' ? readRoleFromStorage(localStorage) : '';
  const userRole = contextRole || storageRole;

  const decision = resolveProtectedRouteDecision({
    token,
    userRole,
    requiredRoles,
    isAuthLoading: isLoading && !userRole,
  });

  if (decision.action === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-neutral-950">
        <Loader2 className="w-8 h-8 text-teal-600 animate-spin" aria-label="Carregando autenticação" />
      </div>
    );
  }

  if (decision.action === 'login') {
    if (import.meta.env.DEV) {
      console.warn('[ProtectedRoute] Sem token. Redirecionando para /login com returnUrl');
    }
    return <Navigate to={buildLoginRedirect(location.pathname, location.search)} replace />;
  }

  if (decision.action === 'forbidden') {
    if (import.meta.env.DEV) {
      console.warn(
        `[ProtectedRoute] FORBIDDEN esperado por role incorreta. Requer: ${decision.requiredRoles.join(',')}, mas o userRole é: "${decision.userRole}". Redirecionando para ${decision.fallbackRoute}`,
      );
    }
    return <Navigate to={decision.fallbackRoute} replace />;
  }

  return <>{children}</>;
}
